import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Trash2, CheckCircle2, AlertCircle, Clock } from "lucide-react";

interface MessageTrace {
  traceId: string;
  wamid: string;
  phoneNumber: string;
  timestamp: string;
  stage: string;
  details: Record<string, any>;
}

export default function MessageTracerDashboard() {
  const [traces, setTraces] = useState<MessageTrace[]>([]);
  const [selectedTrace, setSelectedTrace] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const { data: allTraces, refetch } = trpc.messageTracer.getAllTraces.useQuery();
  const { mutate: clearTraces } = trpc.messageTracer.clearTraces.useMutation({
    onSuccess: () => {
      setTraces([]);
      setSelectedTrace(null);
      refetch();
    },
  });

  useEffect(() => {
    if (allTraces) {
      setTraces(allTraces);
    }
  }, [allTraces]);

  // Auto-refresh a cada 2 segundos
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      refetch();
    }, 2000);

    return () => clearInterval(interval);
  }, [autoRefresh, refetch]);

  const getStageIcon = (stage: string) => {
    switch (stage) {
      case "webhook_received":
        return <Clock className="w-4 h-4 text-blue-500" />;
      case "contact_created":
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case "conversation_created":
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case "message_saved":
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case "error":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case "webhook_received":
        return "bg-blue-50 border-blue-200";
      case "contact_created":
        return "bg-green-50 border-green-200";
      case "conversation_created":
        return "bg-green-50 border-green-200";
      case "message_saved":
        return "bg-green-50 border-green-200";
      case "error":
        return "bg-red-50 border-red-200";
      default:
        return "bg-gray-50 border-gray-200";
    }
  };

  // Agrupar traces por traceId
  const groupedTraces = new Map<string, MessageTrace[]>();
  for (const trace of traces) {
    if (!groupedTraces.has(trace.traceId)) {
      groupedTraces.set(trace.traceId, []);
    }
    groupedTraces.get(trace.traceId)!.push(trace);
  }

  const selectedTraceData = selectedTrace ? groupedTraces.get(selectedTrace) || [] : [];

  return (
    <PermissionGuard permission="reports:message_tracer">
    <DashboardLayout>
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Message Tracer Dashboard</h1>
          <p className="text-gray-600">Rastreamento ponta a ponta de mensagens WhatsApp</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => refetch()}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </Button>
          <Button
            onClick={() => setAutoRefresh(!autoRefresh)}
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
          >
            {autoRefresh ? "Auto ✓" : "Auto ✗"}
          </Button>
          <Button
            onClick={() => clearTraces()}
            variant="destructive"
            size="sm"
            className="gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Limpar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total de Mensagens</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{groupedTraces.size}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Sucesso</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {Array.from(groupedTraces.values()).filter(
                (t) => t[t.length - 1]?.stage === "message_saved"
              ).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Erros</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {Array.from(groupedTraces.values()).filter(
                (t) => t[t.length - 1]?.stage === "error"
              ).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Em Processamento</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {Array.from(groupedTraces.values()).filter(
                (t) => !["message_saved", "error"].includes(t[t.length - 1]?.stage)
              ).length}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Lista de Mensagens */}
        <div className="col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Mensagens Rastreadas</CardTitle>
              <CardDescription>{groupedTraces.size} total</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {Array.from(groupedTraces.entries())
                  .sort((a, b) => {
                    const timeA = new Date(a[1][a[1].length - 1]?.timestamp || 0).getTime();
                    const timeB = new Date(b[1][b[1].length - 1]?.timestamp || 0).getTime();
                    return timeB - timeA;
                  })
                  .map(([traceId, traceList]) => {
                    const lastTrace = traceList[traceList.length - 1];
                    const isSuccess = lastTrace?.stage === "message_saved";
                    const isError = lastTrace?.stage === "error";

                    return (
                      <button
                        key={traceId}
                        onClick={() => setSelectedTrace(traceId)}
                        className={`w-full p-3 rounded-lg border-2 text-left transition-colors ${
                          selectedTrace === traceId
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 hover:border-gray-300"
                        } ${isError ? "bg-red-50" : isSuccess ? "bg-green-50" : ""}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          {getStageIcon(lastTrace?.stage || "unknown")}
                          <span className="font-mono text-xs text-gray-600">{traceId}</span>
                        </div>
                        <div className="text-sm font-medium">{lastTrace?.phoneNumber}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {lastTrace?.wamid?.substring(0, 15)}...
                        </div>
                      </button>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Detalhes da Mensagem Selecionada */}
        <div className="col-span-2">
          {selectedTrace ? (
            <Card>
              <CardHeader>
                <CardTitle>Detalhes do Rastreamento</CardTitle>
                <CardDescription>{selectedTrace}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {selectedTraceData.map((trace, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border-2 ${getStageColor(trace.stage)}`}>
                      <div className="flex items-center gap-3 mb-2">
                        {getStageIcon(trace.stage)}
                        <div>
                          <div className="font-semibold">{trace.stage}</div>
                          <div className="text-xs text-gray-600">
                            {new Date(trace.timestamp).toLocaleTimeString("pt-BR")}
                          </div>
                        </div>
                      </div>

                      {Object.keys(trace.details).length > 0 && (
                        <div className="mt-3 space-y-1 text-sm">
                          {Object.entries(trace.details).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                              <span className="text-gray-600">{key}:</span>
                              <span className="font-mono text-gray-900">
                                {typeof value === "string" ? value : JSON.stringify(value)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-gray-500 py-8">
                  Selecione uma mensagem para ver os detalhes do rastreamento
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
    </DashboardLayout>
    </PermissionGuard>
  );
}
